/**
 * The SVG the two hierarchies are drawn in.
 *
 * Hand-rolled, on purpose. A diagram library was considered and declined: this
 * needs boxes, cubic traces, a viewBox and one drag gesture, and every library
 * that does those brings a layout engine we would then have to fight, plus a
 * dependency on the LAN-deployed bundle. Everything positional comes from
 * `layout.ts`, so this file only turns numbers into elements.
 *
 * Two decisions worth knowing about:
 *
 * - Every box is a real focusable DOM node with an aria-label, not a canvas
 *   draw call. That is the reason for SVG rather than <canvas>: a diagram of
 *   requirements is a list of requirements, and it should be readable as one.
 *
 * - The drop target is whatever the pointer last ENTERED, not whatever the
 *   pointer's coordinates land inside. Hit-testing by coordinate needs the
 *   element's on-screen rectangle, which jsdom reports as zero, so the refusal
 *   this whole interaction exists to make would be untestable. Pointer enter
 *   and leave are events the browser already computes correctly.
 *
 * Since issue #93 a box that has something under it carries a FOLD toggle on its
 * top-right corner - "+12" when it is hiding twelve boxes, "−" when it is showing
 * them all - and zooming in is limited by how big a box is drawn rather than by
 * how big the whole diagram is, so a wide diagram can still be zoomed to legible.
 * Which boxes are folded is decided in `focus.ts`; this file only draws it.
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import AspectRatioIcon from '@mui/icons-material/AspectRatio'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import RemoveIcon from '@mui/icons-material/Remove'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { MONOSPACE } from '../../theme'
import type { Fold } from './focus'
import type { Diagram, PlacedNode } from './layout'
import { actualSize, fitToPane, fitViewBox, panBy, zoomAt } from './viewbox'
import type { ViewBox } from './viewbox'

/** How a box may be dragged onto another, or why it may not be. */
export interface ReparentOffer {
  allowed: boolean
  /** Shown on the disabled handle. Never hidden — see contract §5.1. */
  reason: string
  /** What to call the drag. "Re-parent" is jargon; this is the model's word. */
  verb: string
  onDrop: (childKey: string, parentKey: string) => void
}

export default function DiagramCanvas({
  diagram,
  reparent,
  onOpen,
  emptyLabel,
  folds,
  onToggle,
}: {
  diagram: Diagram
  /** Null when this diagram cannot be edited at all — the parts tree. */
  reparent: ReparentOffer | null
  /** Clicking a box. Given the model ID, which may be ''. */
  onOpen?: (node: PlacedNode) => void
  emptyLabel: string
  /** What `focus.fold` said about each drawn box with children. Absent: no toggles. */
  folds?: ReadonlyMap<string, Fold>
  /** A box's fold toggle was used. */
  onToggle?: (key: string, state: Fold) => void
}) {
  const theme = useTheme()
  const svgRef = useRef<SVGSVGElement | null>(null)

  // The pane's size on screen, kept current as it resizes; 0 before layout and
  // always under jsdom, which has no ResizeObserver. The fitted box is widened
  // to the pane's shape (`fitToPane`), so the viewBox's width IS the drawn
  // scale - the zoom limits, actual size and pan speed all rely on that.
  const [pane, setPane] = useState({ width: 0, height: 0 })
  const hasNodes = diagram.nodes.length > 0
  useLayoutEffect(() => {
    const element = svgRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const { width, height } = element.getBoundingClientRect()
      setPane((now) => (now.width === width && now.height === height ? now : { width, height }))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [hasNodes])

  const base = useMemo(
    () => fitToPane(fitViewBox(diagram.width, diagram.height), pane.width, pane.height),
    [diagram, pane],
  )
  const [box, setBox] = useState<ViewBox | null>(null)
  const view = box ?? base

  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const panning = useRef<{ x: number; y: number } | null>(null)

  /** Diagram units per on-screen pixel. 1 before the element has been laid out. */
  const perPixel = () => {
    const rect = svgRef.current?.getBoundingClientRect()
    return rect && rect.width ? view.width / rect.width : 1
  }

  const zoom = (factor: number) => setBox(zoomAt(view, factor, centre(), base, pane.width))

  const centre = () => ({ x: view.x + view.width / 2, y: view.y + view.height / 2 })

  const tone = (node: PlacedNode) => {
    if (node.tone === 'unresolved') return theme.palette.warning.main
    if (node.tone === 'definition') return theme.palette.secondary.main
    if (node.isOrphan) return theme.palette.error.main
    return theme.palette.primary.main
  }

  if (diagram.nodes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        {emptyLabel}
      </Typography>
    )
  }

  return (
    <Box>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
        <Tooltip title="Zoom in">
          <IconButton size="small" onClick={() => zoom(1.25)}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out">
          <IconButton size="small" onClick={() => zoom(1 / 1.25)}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit the whole diagram">
          <IconButton size="small" onClick={() => setBox(null)}>
            <CenterFocusStrongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Actual size: boxes at their natural size, centred where you are">
          <IconButton
            size="small"
            onClick={() => {
              // Unmeasured (jsdom), there is no natural size to go to.
              if (pane.width > 0 && pane.height > 0) {
                setBox(actualSize(centre(), pane.width, pane.height))
              }
            }}
          >
            <AspectRatioIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          Drag the background to pan.
          {reparent
            ? ` Drag a box’s handle onto another and it ${reparent.verb} that one too.`
            : ' Containment is structural here, so it is not editable from a diagram.'}
        </Typography>
      </Stack>

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <svg
          ref={svgRef}
          role="img"
          aria-label="Hierarchy diagram"
          viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`}
          // Taller than it was (a fixed 520): a wrapped band makes a diagram
          // tall rather than wide, and a short pane wastes the width it has.
          style={{
            width: '100%',
            height: 'clamp(420px, 65vh, 760px)',
            display: 'block',
            touchAction: 'none',
          }}
          onPointerMove={(event) => {
            const from = panning.current
            if (!from) return
            const k = perPixel()
            setBox(panBy(view, (event.clientX - from.x) * k, (event.clientY - from.y) * k))
            panning.current = { x: event.clientX, y: event.clientY }
          }}
          onPointerUp={() => {
            panning.current = null
            // A drag that ended over nothing is a cancelled drag, not an error:
            // it is how somebody backs out once they see what they are about to
            // do.
            if (dragging && over && over !== dragging) reparent?.onDrop(dragging, over)
            setDragging(null)
            setOver(null)
          }}
          onPointerLeave={() => {
            panning.current = null
            setDragging(null)
            setOver(null)
          }}
        >
          <defs>
            <marker
              id="best-trace-head"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={theme.palette.text.secondary} />
            </marker>
          </defs>

          {/* The pan surface. Sized to the fitted box rather than to the view,
              so it is still there after zooming out. */}
          <rect
            data-testid="diagram-background"
            x={base.x}
            y={base.y}
            width={base.width}
            height={base.height}
            fill="transparent"
            onPointerDown={(event) => {
              panning.current = { x: event.clientX, y: event.clientY }
            }}
          />

          {diagram.bands.map((band) => (
            <text
              key={band.band}
              x={0}
              y={band.y + band.height / 2 + 5}
              fill={theme.palette.text.secondary}
              fontSize={14}
              fontFamily={MONOSPACE}
            >
              {band.label}
            </text>
          ))}

          {diagram.edges.map((edge) => (
            <path
              key={edge.key}
              data-testid={`trace-${edge.key}`}
              d={edge.path}
              fill="none"
              stroke={theme.palette.text.secondary}
              strokeWidth={1.5}
              markerEnd="url(#best-trace-head)"
              opacity={0.7}
            />
          ))}

          {diagram.nodes.map((node) => {
            const colour = tone(node)
            const isTarget = dragging !== null && over === node.key && over !== dragging
            const state = folds?.get(node.key)
            return (
              <g key={node.key}>
                <rect
                  x={node.x}
                  y={node.y}
                  width={node.width}
                  height={node.height}
                  rx={6}
                  fill={theme.palette.background.default}
                  stroke={isTarget ? theme.palette.success.main : colour}
                  strokeWidth={isTarget ? 3 : 1.5}
                  strokeDasharray={node.isOrphan ? '5 3' : undefined}
                  role="button"
                  tabIndex={0}
                  aria-label={`${node.label}${node.isOrphan ? ' (orphan)' : ''}`}
                  style={{ cursor: onOpen ? 'pointer' : 'default' }}
                  // pointerover/pointerout rather than the enter/leave pair.
                  // Enter and leave do not bubble, so React synthesises them
                  // from these two anyway - and a synthesised event is one a
                  // test cannot dispatch, which would make the cycle refusal
                  // that this whole interaction exists for untestable.
                  onPointerOver={() => {
                    if (dragging) setOver(node.key)
                  }}
                  onPointerOut={() => {
                    if (dragging && over === node.key) setOver(null)
                  }}
                  onClick={() => onOpen?.(node)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') onOpen?.(node)
                  }}
                >
                  <title>{`${node.label} — ${node.detail}`}</title>
                </rect>

                <text
                  x={node.x + 10}
                  y={node.y + 22}
                  fill={theme.palette.text.primary}
                  fontSize={13}
                  fontFamily={MONOSPACE}
                  style={{ pointerEvents: 'none' }}
                >
                  {node.label}
                </text>
                <text
                  x={node.x + 10}
                  y={node.y + 40}
                  fill={theme.palette.text.secondary}
                  fontSize={11}
                  style={{ pointerEvents: 'none' }}
                >
                  {clip(node.detail, 26)}
                </text>
                {node.isOrphan && (
                  <text
                    x={node.x + node.width - 10}
                    y={node.y + node.height - 6}
                    textAnchor="end"
                    fill={theme.palette.error.main}
                    fontSize={10}
                    style={{ pointerEvents: 'none' }}
                  >
                    orphan
                  </text>
                )}

                {reparent && (
                  // Present whether or not the write is permitted, and disabled
                  // with the reason when it is not. A missing handle is
                  // indistinguishable from a diagram that cannot do this.
                  <circle
                    data-testid={`handle-${node.key}`}
                    cx={node.x + node.width / 2}
                    cy={node.y + node.height}
                    r={7}
                    fill={
                      reparent.allowed
                        ? dragging === node.key
                          ? theme.palette.success.main
                          : colour
                        : theme.palette.action.disabled
                    }
                    stroke={theme.palette.background.paper}
                    strokeWidth={2}
                    role="button"
                    tabIndex={reparent.allowed ? 0 : -1}
                    aria-disabled={!reparent.allowed}
                    aria-label={
                      reparent.allowed
                        ? `Drag to add another box that ${node.label} ${reparent.verb}`
                        : `Cannot add another box that ${node.label} ${reparent.verb}: ${reparent.reason}`
                    }
                    style={{ cursor: reparent.allowed ? 'grab' : 'not-allowed' }}
                    onPointerDown={(event) => {
                      if (!reparent.allowed) return
                      event.stopPropagation()
                      setDragging(node.key)
                    }}
                  >
                    <title>
                      {reparent.allowed
                        ? `Drag onto another box so that ${node.label} ${reparent.verb} it too`
                        : reparent.reason}
                    </title>
                  </circle>
                )}

                {state && onToggle && (
                  <FoldToggle node={node} state={state} colour={colour} onToggle={onToggle} />
                )}
              </g>
            )
          })}

          {/* The trace being proposed. Drawn to the box under the pointer rather
              than to the pointer itself, which is what makes the target
              unambiguous. */}
          {dragging && over && over !== dragging && (
            <GhostTrace diagram={diagram} fromKey={dragging} toKey={over} colour={theme.palette.success.main} />
          )}
        </svg>
      </Box>
    </Box>
  )
}

/**
 * The pill on a box's top-right corner that folds what hangs under it.
 *
 * Straddles the top edge, clear of the label and of the traces, which arrive at
 * the middle. A sibling of the box rather than inside it, so clicking it never
 * opens the element - and it stops the pointer reaching the pan surface.
 */
function FoldToggle({
  node,
  state,
  colour,
  onToggle,
}: {
  node: PlacedNode
  state: Fold
  colour: string
  onToggle: (key: string, state: Fold) => void
}) {
  const theme = useTheme()
  const text = state.hidden > 0 ? `+${state.hidden}` : '−'
  const width = 12 + text.length * 7
  const x = node.x + node.width - width - 8
  const y = node.y - 9
  const label =
    state.hidden > 0
      ? `Show ${state.hidden} hidden under ${node.label}`
      : `Hide what is under ${node.label}`
  const act = () => onToggle(node.key, state)
  return (
    <g
      data-testid={`fold-${node.key}`}
      role="button"
      tabIndex={0}
      aria-label={label}
      style={{ cursor: 'pointer' }}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={act}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          act()
        }
      }}
    >
      <title>{label}</title>
      <rect
        x={x}
        y={y}
        width={width}
        height={18}
        rx={9}
        fill={state.hidden > 0 ? colour : theme.palette.background.paper}
        stroke={colour}
        strokeWidth={1.5}
      />
      <text
        x={x + width / 2}
        y={y + 13}
        textAnchor="middle"
        fontSize={11}
        fontFamily={MONOSPACE}
        fill={state.hidden > 0 ? theme.palette.getContrastText(colour) : colour}
        style={{ pointerEvents: 'none' }}
      >
        {text}
      </text>
    </g>
  )
}

/** The dashed line from the dragged box to the one it is over. */
function GhostTrace({
  diagram,
  fromKey,
  toKey,
  colour,
}: {
  diagram: Diagram
  fromKey: string
  toKey: string
  colour: string
}) {
  const from = diagram.nodes.find((node) => node.key === fromKey)
  const to = diagram.nodes.find((node) => node.key === toKey)
  if (!from || !to) return null
  return (
    <line
      data-testid="ghost-trace"
      x1={from.x + from.width / 2}
      y1={from.y + from.height / 2}
      x2={to.x + to.width / 2}
      y2={to.y + to.height / 2}
      stroke={colour}
      strokeWidth={2}
      strokeDasharray="6 4"
    />
  )
}

/** SVG text does not wrap, so a long statement is cut with the full text in a title. */
function clip(text: string, chars: number): string {
  return text.length > chars ? `${text.slice(0, chars - 1)}…` : text
}
